interface InnerHeroProps {
  badge: string;
  h1: string;
  subtitle: string;
  bgImage: string;
  onOpenConsultation: () => void;
}

export default function InnerHeroSection({ badge, h1, subtitle, bgImage, onOpenConsultation }: InnerHeroProps) {
  const parts = h1.split('|');

  return (
    <section className="relative min-h-[72vh] flex items-center overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src={bgImage}
          alt={h1.replace('|', ' ')}
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F2A44]/94 via-[#0F2A44]/74 to-[#0F2A44]/25" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F2A44]/45 via-transparent to-[#0F2A44]/15" />
      </div>

      {/* Decorative line */}
      <div className="absolute top-0 left-0 w-px h-full bg-gradient-to-b from-transparent via-gold/15 to-transparent pointer-events-none" />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pt-28 pb-20">
        <div className="max-w-2xl">
          {/* Badge */}
          <div className="inline-flex items-center gap-2.5 bg-white/10 backdrop-blur-md border border-white/20 text-white/90 text-xs font-bold uppercase tracking-[0.18em] px-5 py-2.5 rounded-full mb-6">
            <i className="ri-shield-check-fill text-gold"></i>
            {badge}
          </div>

          {/* H1 */}
          <h1 className="font-heading text-white font-bold leading-[1.05] mb-6" style={{ fontSize: 'clamp(2.2rem, 4vw, 3.8rem)' }}>
            {parts.length > 1 ? (
              <>
                {parts[0]}{' '}
                <em className="text-gold not-italic">{parts[1]}</em>
              </>
            ) : h1}
          </h1>

          {/* Subtitle */}
          <p className="text-white/72 text-lg leading-relaxed mb-9 max-w-xl">
            {subtitle}
          </p>

          {/* CTAs */}
          <div className="flex flex-wrap gap-4 mb-9">
            <button
              onClick={onOpenConsultation}
              className="whitespace-nowrap group bg-gold text-navy font-bold px-9 py-4 rounded-full text-base hover:brightness-110 transition-all cursor-pointer flex items-center gap-2.5"
            >
              Get Free Consultation
              <span className="w-5 h-5 flex items-center justify-center group-hover:translate-x-1 transition-transform">
                <i className="ri-arrow-right-line"></i>
              </span>
            </button>
            <a
              href="tel:2173462042"
              className="whitespace-nowrap flex items-center gap-3 bg-white/12 backdrop-blur-sm text-white font-semibold px-8 py-4 rounded-full text-base hover:bg-white/22 transition-all border border-white/28"
            >
              <span className="w-6 h-6 flex items-center justify-center bg-gold/25 rounded-full">
                <i className="ri-phone-fill text-gold text-sm"></i>
              </span>
              (217) 346-2042
            </a>
          </div>

          {/* Trust micro-row */}
          <div className="flex flex-wrap gap-6 pt-5 border-t border-white/12">
            {[
              { icon: 'ri-shield-check-fill', text: 'Licensed & Insured' },
              { icon: 'ri-fingerprint-line', text: 'Background Checked' },
              { icon: 'ri-star-fill', text: '5-Star Google Rated' },
            ].map((item) => (
              <div key={item.text} className="flex items-center gap-2 text-white/60 text-sm">
                <i className={`${item.icon} text-gold text-xs`}></i>
                {item.text}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Floating contact card */}
      <div className="absolute bottom-8 right-8 hidden lg:block z-10">
        <div className="bg-white rounded-2xl px-6 py-6 w-64 border border-gray-100" style={{ boxShadow: '0 16px 50px rgba(15,42,68,0.22)' }}>
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
            <p className="text-navy/45 text-xs font-bold uppercase tracking-widest">Available Now</p>
          </div>
          <a href="tel:2173462042" className="font-heading text-navy text-lg font-bold hover:text-gold transition-colors flex items-center gap-2.5 mb-1">
            <span className="w-8 h-8 flex items-center justify-center bg-gold/15 rounded-full">
              <i className="ri-phone-fill text-gold text-sm"></i>
            </span>
            (217) 346-2042
          </a>
          <p className="text-navy/35 text-xs pl-11">Mon – Sun, 8am – 6pm</p>
        </div>
      </div>
    </section>
  );
}
